import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Settings2, SlidersHorizontal, BarChart3, Target, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { initConsent, saveConsent, logImpression, getConsent } from "@/lib/consent";

const ROWS = [
  { k: "necessary", icon: Settings2, bg: "bg-emerald-500", t: "Të domosdoshme", d: "Të nevojshme për funksionimin bazë të faqes (kyçja, siguria, ruajtja e zgjedhjes për cookies). Nuk mund të çaktivizohen." },
  { k: "preferences", icon: SlidersHorizontal, bg: "bg-amber-400", t: "Preferenca", d: "Ruajnë gjuhën, qytetin dhe preferencat e tua." },
  { k: "analytics", icon: BarChart3, bg: "bg-blue-500", t: "Analitika", d: "Na ndihmojnë të kuptojmë si përdoret faqja (statistikat e GjejShqip dhe Google Analytics)." },
  { k: "marketing", icon: Target, bg: "bg-rose-500", t: "Marketing dhe reklama", d: "Përdoren për matjen dhe personalizimin e reklamave (p.sh. Google Ads)." },
];

function Settings({ initial, onSave, onClose, onWithdraw }) {
  const [c, setC] = useState({ preferences: false, analytics: false, marketing: false, ...(initial || {}) });
  return (
    <div className="fixed inset-0 z-[3000] bg-black/50 flex items-end sm:items-center justify-center" data-testid="cookie-settings">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto relative">
        <button onClick={onClose} data-testid="cookie-settings-close" aria-label="Mbyll" className="absolute top-4 right-4 text-slate-400"><X className="w-6 h-6" /></button>
        <h2 className="text-xl font-extrabold text-slate-900 pr-8">Rregullo preferencat e cookies</h2>
        <p className="text-sm text-slate-500 mt-1">Mund të zgjedhësh cilat kategori cookies dëshiron të lejosh. Cookies jo të domosdoshme nuk aktivizohen pa pëlqimin tënd.</p>
        <div className="space-y-2.5 mt-4">
          {ROWS.map((r) => (
            <div key={r.k} className="flex items-start gap-3 bg-slate-50 rounded-2xl p-3 border border-slate-100">
              <span className={`w-11 h-11 shrink-0 rounded-full ${r.bg} text-white flex items-center justify-center`}><r.icon className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0"><div className="font-bold text-slate-900">{r.t}</div><div className="text-xs text-slate-500 mt-0.5">{r.d}</div></div>
              {r.k === "necessary" ? <Lock className="w-5 h-5 text-slate-400 mt-1" />
                : <Switch data-testid={`cookie-toggle-${r.k}`} checked={!!c[r.k]} onCheckedChange={(v) => setC({ ...c, [r.k]: v })} className="data-[state=checked]:bg-emerald-500 mt-1" />}
            </div>
          ))}
        </div>
        <button data-testid="cookie-save" onClick={() => onSave(c)} className="btn-3d-green w-full h-12 rounded-2xl font-bold mt-4 mb-1">Ruaj preferencat</button>
        {onWithdraw && <button data-testid="cookie-withdraw" onClick={onWithdraw} className="w-full h-11 rounded-2xl font-semibold text-rose-600 mt-2">Tërhiq pëlqimin (refuzo të gjitha)</button>}
        <Link to="/politika-cookies" onClick={onClose} className="block text-center text-sm text-emerald-700 underline mt-2" data-testid="cookie-policy-link-settings">Politika e Cookies</Link>
      </div>
    </div>
  );
}

export function CookieBanner() {
  const [show, setShow] = useState(false);
  const [settings, setSettings] = useState(false);
  useEffect(() => {
    initConsent().then((ok) => { if (!ok) { setShow(true); logImpression(); } });
    const open = () => setSettings(true);
    window.addEventListener("gs-open-cookies", open);
    return () => window.removeEventListener("gs-open-cookies", open);
  }, []);
  const done = async (kind, cats) => { await saveConsent(kind, cats); setShow(false); setSettings(false); };
  const all = { preferences: true, analytics: true, marketing: true };
  const prev = getConsent();
  return (
    <>
      {show && !settings && (
        <div className="fixed inset-x-0 bottom-0 z-[2900] p-3 sm:p-4 flex justify-center" data-testid="cookie-banner">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-[0_-8px_40px_rgba(0,0,0,.25)] p-5 text-center border border-slate-100">
            <div className="text-4xl" aria-hidden>🍪</div>
            <h2 className="text-xl font-extrabold text-slate-900 mt-1">Cookies në GjejShqip.com</h2>
            <p className="text-sm text-slate-600 mt-1.5">Ne përdorim cookies për funksionimin e faqes, ruajtjen e preferencave, analizimin e vizitave dhe përmirësimin e shërbimeve tona.</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button data-testid="cookie-accept-all" onClick={() => done("accept_all", all)} className="btn-3d-green h-12 rounded-2xl font-bold text-sm mb-1">Prano të gjitha</button>
              <button data-testid="cookie-reject" onClick={() => done("reject", {})} className="btn-3d-green h-12 rounded-2xl font-bold text-sm mb-1">Refuzo jo të domosdoshmet</button>
            </div>
            <button data-testid="cookie-customize" onClick={() => setSettings(true)} className="btn-3d-soft w-full h-11 rounded-2xl font-bold text-slate-800 mt-2 mb-1">Rregullo preferencat</button>
            <p className="text-xs text-slate-500 mt-3">Më shumë informacion në <Link to="/politika-cookies" className="text-emerald-700 underline" data-testid="cookie-policy-link">Politika e Cookies</Link></p>
          </div>
        </div>
      )}
      {settings && (
        <Settings initial={prev?.cats} onClose={() => setSettings(false)}
          onSave={(c) => done(c.preferences && c.analytics && c.marketing ? "accept_all" : !c.preferences && !c.analytics && !c.marketing ? "reject" : "custom", c)}
          onWithdraw={prev && Object.values(prev.cats || {}).some(Boolean) ? () => done("withdraw", {}) : null} />
      )}
    </>
  );
}

export default CookieBanner;
